import { defineStore } from 'pinia';
import { http } from '@/system/http';
import moment from 'moment';
import { JournalTypes, JournalFilterTypes } from '@/system/helpers/constants';
import DateFilter from '@/system/components/global/table/custom_filters/DateFilter.vue';

// Map dynamic filter fields to custom components when values are empty
const FILTER_COMPONENTS = {
    [JournalFilterTypes.Date]: DateFilter,
    [JournalFilterTypes.TreatmentStartDate]: DateFilter,
    [JournalFilterTypes.TreatmentEndDate]: DateFilter,
};

export const useJournalsStore = defineStore('journalsFilters', {
    state: () => ({
        filters: [],
        allowedFilterConfigs: [],
        loadingAllowed: false,
    }),

    actions: {
        async loadAllowedFilters() {
            if (this.loadingAllowed || this.allowedFilterConfigs.length > 0) return;
            this.loadingAllowed = true;
            try {
                const { data } = await http.get('/app/journal/filters');
                this.allowedFilterConfigs = Array.isArray(data) ? data : [];
            } finally {
                this.loadingAllowed = false;
            }
        },

        async setFilter(filter) {
            if (!filter?.field) return;
            const existing = this.filters.find(f => f.field === filter.field);
            if (existing) {
                existing.value = filter.value;
                existing.operator = filter.operator;
            } else {
                this.filters.push(filter);
            }
        },

        async removeFilter(field) {
            const idx = this.filters.findIndex(f => f.field === field);
            if (idx !== -1) this.filters.splice(idx, 1);
        },
    },

    getters: {
        journalType(state) {
            const f = state.filters.find(f => f.field === JournalTypes.Type);
            return f ? f.value : null;
        },

        dateFilter(state) {
            return state.filters.find(f => f.field === JournalFilterTypes.Date) || null;
        },

        /**
         * Now we are doing this because the surveys are not coming from the journal table, and it has a separate endpoint for surveys.
         * We are using the same filters as the journals, but we are filtering the surveys responses.
         * This is not the best approach, but it is the only way to get the surveys to work with the same filters as the journals.
         * We should refactor this to use the same endpoint for both journals and surveys, and use the same filters for both.
         * I guess, the same endpoint will also include the Journal Files as well.
         */
        filterSurveyResponses: (state) => (surveyRequests) => {
            const typeFilter = state.filters.find(f => f.field === JournalTypes.Type);
            const typeVal = typeFilter?.value || null;

            // If journal_type is explicitly Survey, allow surveys
            if (typeVal === JournalTypes.Survey) {
                return applyDateFilterToSurveys(surveyRequests, state);
            }

            // If any journal-specific filter that can't apply to surveys is present, exclude surveys
            const hasLockedFilter = state.filters.some(f => f.field === JournalFilterTypes.Locked);
            const hasFavoriteFilter = state.filters.some(f => f.field === JournalFilterTypes.Favorite);
            const hasTreatmentStartDateFilter = state.filters.some(f => f.field === JournalFilterTypes.TreatmentStartDate);
            const hasTreatmentEndDateFilter = state.filters.some(f => f.field === JournalFilterTypes.TreatmentEndDate);
            const hasTagFilter = state.filters.some(f => f.field === JournalFilterTypes.Tag);

            if (hasLockedFilter || hasFavoriteFilter || hasTreatmentStartDateFilter || hasTreatmentEndDateFilter || hasTagFilter) {
                return [];
            }

            // If journal_type not set and no incompatible filters, include surveys with date filter
            if (!typeVal) {
                return applyDateFilterToSurveys(surveyRequests, state);
            }

            // journal_type set to something else (e.g., appointment/general_note) → exclude surveys
            return [];
        },

        decoratedAllowed(state) {
            return (state.allowedFilterConfigs || []).map(f => {
                if ((f.values || []).length === 0 && FILTER_COMPONENTS[f.field]) {
                    return { ...f, component: FILTER_COMPONENTS[f.field] };
                }
                return f;
            });
        },

        appliedFilterConfigs(state) {
            const selected = state.filters.map(f => f.field);
            return this.decoratedAllowed.filter(f => selected.includes(f.field));
        },

        allowedFilters(state) {
            const selected = state.filters.map(f => f.field);
            return this.decoratedAllowed.filter(f => !selected.includes(f.field));
        },
    },
});

// Helpers
function applyDateFilterToSurveys(surveyRequests, state) {
    const dateFilter = state.filters.find(f => f.field === JournalFilterTypes.Date);
    if (!dateFilter || !dateFilter.operator || !dateFilter.value) {
        return surveyRequests;
    }

    const op = (dateFilter.operator || '').toLowerCase();
    if (op === 'is' && typeof dateFilter.value === 'string') {
        const target = moment(dateFilter.value, 'YYYY-MM-DD').format('YYYY-MM-DD');
        return surveyRequests.filter(s =>
            moment(s.answered_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD') === target
        );
    }

    if (op === 'between' && dateFilter.value.start && dateFilter.value.end) {
        const start = moment(dateFilter.value.start, 'YYYY-MM-DD').startOf('day');
        const end = moment(dateFilter.value.end, 'YYYY-MM-DD').endOf('day');
        return surveyRequests.filter(s => {
            const ans = moment(s.answered_at, 'YYYY-MM-DD HH:mm:ss');
            return ans.isSameOrAfter(start) && ans.isSameOrBefore(end);
        });
    }

    return surveyRequests;
}


